import { InfoIcon, Label, Select, Tag, Tooltip } from '@lokalise/louis'
import type { Integrations } from '@lokalise/translation-api-schemas'
import { components, type OptionProps } from 'react-select'
import { type AiConfigModelDetail, useAiConfigModels } from '../../hooks/useAiConfigModels'
import { AiModelIcon } from '../AiModelIcon/AiModelIcon'
import styles from './AiModelSelect.module.css'

type AiModelSelectProps = {
  selectedModel: Integrations
  onModelChange: (model: Integrations) => void
}

export const AiModelSelect = ({ selectedModel, onModelChange }: AiModelSelectProps) => {
  const { details, isLoading } = useAiConfigModels()

  return (
    <Label
      text={
        <div className="flex gap-1">
          <span className={styles.heading}>LLM provider settings</span>
          <Tooltip
            classNames={{ content: styles.tooltip }}
            placement="bottom"
            tooltip="The routing option applies to all languages in the project and automatically selects the most suitable engine from the available models to ensure the highest possible translation quality. While you can configure it to use a single LLM, this may result in lower-quality translations."
          >
            <span className="flex items-center">
              <InfoIcon size="16px" ariaLabel="Information about LLM provider settings" />
            </span>
          </Tooltip>
        </div>
      }
    >
      <Select
        required
        isLoading={isLoading}
        options={details ?? []}
        value={details?.find(({ integration }) => integration === selectedModel)}
        onChange={(detail) => detail && onModelChange(detail.integration)}
        getOptionLabel={(option) => `${option.name} (${option.description})`}
        getOptionValue={(option) => option.integration ?? ''}
        components={{ Option: CustomOption }}
      />
    </Label>
  )
}

const CustomOption = (props: OptionProps<AiConfigModelDetail, false>) => {
  const { label: optionLabel, data } = props

  return (
    <components.Option {...props}>
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2 items-center">
          <AiModelIcon integration={data.integration} />
          <span>{optionLabel}</span>
        </div>
        {/* null is used to refer to the default dynamic routing option */}
        {data.integration === null && (
          <Tag className={styles.tag} variant="solid">
            Recommended
          </Tag>
        )}
      </div>
    </components.Option>
  )
}
